import classNames from "classnames/dedupe";
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ReactDOM from "react-dom";
import {
  useBodyOverflow,
  useEventListener,
  useMountedState,
  useOnClickOutside,
} from "../../../hooks";
import { ExitIcon } from "../../../icons";
import { Typography } from "../../Fundamentals/Typography";
import DrawerMask from "../../Utility/DrawerMask/DrawerMask";
import { IDrawerProps } from "./Drawer.interface";

const MAX_ANIMATION_DELAY = 1000;
export const DEFAULT_DRAWER_CONTAINER_SELECTOR = "#root-drawer";

export const Drawer: FC<IDrawerProps> = ({
  header = "",
  headerComponent = null,
  navigationComponent,
  children,
  opened,
  position = "right",
  customStyle,
  maskCustomStyle,
  mask = true,
  useClickOutside = true,
  portalContainerSelector,
  classes,
  render,
  onClose,
}) => {
  const drawerContentRef = useRef();
  const drawerMaskRef = useRef<HTMLDivElement>();
  const tm = useRef<NodeJS.Timeout>();
  const [drawerVisibility, setDrawerVisibility] = useState(opened);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(opened);

  useBodyOverflow(drawerVisibility);

  const clearTimers = useCallback(() => {
    if (tm?.current) {
      clearTimeout(tm?.current);
      tm.current = null;
    }
  }, []);

  const handleVisibility = useCallback(() => {
    clearTimers();
    setDrawerVisibility(opened);
  }, [clearTimers, opened]);

  useEventListener(
    "transitionend",
    handleVisibility,
    undefined,
    drawerContentRef,
  );
  useEventListener(
    "transitioncancel",
    handleVisibility,
    undefined,
    drawerContentRef,
  );

  useEffect(() => {
    clearTimers();
    setDrawerOpen(opened);
    if (opened) {
      setDrawerVisibility(true);
    }
    if (!opened) {
      /** Fallback to timeout if transitions will not fire */
      tm.current = setTimeout(() => {
        setDrawerVisibility(opened);
      }, MAX_ANIMATION_DELAY);
    }
    return () => {
      clearTimers();
    };
  }, [clearTimers, opened]);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    onClose && onClose();
  }, [onClose]);

  const handleClickOutside = useCallback(() => {
    if (drawerOpen) {
      handleCloseDrawer();
    }
  }, [handleCloseDrawer, drawerOpen]);

  useOnClickOutside(
    useClickOutside && handleClickOutside,
    "mousedown",
    drawerContentRef,
  );

  // Separate state to toggle display: none for drawer
  const drawerClasses = classNames("drawer", classes?.root, {
    "drawer--open": drawerVisibility,
  });

  // map position to class name
  const positionClassName = position;

  const contentClasses = classNames(
    "drawer__content-wrapper",
    drawerOpen ? "slide-in" : `slide-out-${positionClassName}`,
    `drawer__content-wrapper--position-${positionClassName}`,
  );

  const bodyClasses = classNames("drawer__body", classes?.body);

  const isMountedState = useMountedState();

  const container: Element | null = useMemo(() => {
    if (!isMountedState) {
      return null;
    }
    const el =
      document.querySelector(
        portalContainerSelector || DEFAULT_DRAWER_CONTAINER_SELECTOR,
      ) || document.querySelector("body");
    if (!el) return null;
    return el;
  }, [isMountedState, portalContainerSelector]);

  const body = render
    ? render({
        opened: drawerOpen,
        visible: drawerVisibility,
        close: handleCloseDrawer,
      })
    : children;

  return isMountedState && container
    ? ReactDOM.createPortal(
        <div className={drawerClasses} data-testid="Drawer__root">
          {mask && (
            <DrawerMask
              ref={drawerMaskRef}
              classes={{
                root: classNames(drawerOpen ? "fade-in" : "fade-out"),
              }}
              mods={["drawer"]}
              data-testid="Drawer__mask"
              style={maskCustomStyle}
            />
          )}
          <div
            className={contentClasses}
            ref={drawerContentRef}
            data-testid="Drawer__content-wrapper"
            style={customStyle}
          >
            <div className={`drawer__content ${classes?.content || ""}`}>
              <div className="drawer__wrapper-body">
                {/* Header */}
                <div className="drawer__header">
                  <div className="drawer__header-top">
                    {headerComponent || (
                      <Typography classes={{ root: "drawer__title" }}>
                        {header}
                      </Typography>
                    )}
                    <div
                      className="drawer__close"
                      data-testid="Drawer__close"
                      onClick={handleCloseDrawer}
                    >
                      <ExitIcon />
                    </div>
                  </div>
                  {navigationComponent && (
                    <div className="drawer__header-nav">
                      {" "}
                      {navigationComponent}
                    </div>
                  )}
                </div>
                {/* Body */}
                <div className={bodyClasses}>{body}</div>
              </div>
            </div>
          </div>
        </div>,
        container,
      )
    : null;
};

export default Drawer;
